import CryptoJS from "crypto-js";

// ====================== PUBLIC KEY ======================
const PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAsGb3hlHoVqNed+yBzpBq
U6syIIrDzTbGaZO/bDKnlywSXH6sTkFbbNeQHMDxWA1JM+g2qvUxAFyI6Duz7YNB
bYYJjg/nnHHn+GDLj/WeI+qpyEWFLZrF+m9SsZ9ThQ5SF+vCX5q8d0Ghu2eaemD5
zXlK3J9PvhvFkwkskaIo2mrywoZprCqZAmpxCkEay974USkK4RJ+H3cm/I5z4ews
9a9qrhzQ1B79GEP8AlTFJ6owbilnOtcvPMHcOuAcE+AyX7hfYQwoFC0UWosL0esM
s5GDQXxbq3j46txwIuTFHmF9ni/KwlPSDgGaU724chBnqRLPxspgjepScIyeh9U3
FwIDAQAB
-----END PUBLIC KEY-----`;

// Cache RSA public key
let cachedPublicKey = null;

// Store the last generated AES key & IV (for decrypting backend response)
let lastAesKeyRaw = null; // Uint8Array(32)
let lastIvHex = null; // string like "a1b2c3..."

const getPublicKey = async () => {
  if (cachedPublicKey) return cachedPublicKey;
  const pem = PUBLIC_KEY_PEM.replace(
    /-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\s+/g,
    "",
  );
  const binaryDer = Uint8Array.from(atob(pem), (c) => c.charCodeAt(0));
  cachedPublicKey = await crypto.subtle.importKey(
    "spki",
    binaryDer.buffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
  return cachedPublicKey;
};

// ====================== ENCRYPT LOGIN / ANY PAYLOAD ======================
export const encryptLogin = async (payload) => {
  try {
    const publicKey = await getPublicKey();

    // Generate random AES key (32 bytes) and IV (16 bytes)
    const aesKeyRaw = crypto.getRandomValues(new Uint8Array(32));
    const aesKeyHex = Array.from(aesKeyRaw)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    const ivRaw = crypto.getRandomValues(new Uint8Array(16));
    const ivHex = Array.from(ivRaw)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    // AES encrypt payload using CryptoJS (matches backend)
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      CryptoJS.enc.Hex.parse(aesKeyHex),
      {
        iv: CryptoJS.enc.Hex.parse(ivHex),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    ).toString();

    // RSA encrypt the RAW 32-byte AES key
    const encryptedKeyBuffer = await crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      publicKey,
      aesKeyRaw.buffer,
    );

    const encryptedKeyBase64 = btoa(
      String.fromCharCode(...new Uint8Array(encryptedKeyBuffer)),
    );

    // SAVE for decrypting response!
    lastAesKeyRaw = aesKeyRaw;
    lastIvHex = ivHex;

    return {
      encryptedKey: encryptedKeyBase64,
      iv: ivHex,
      data: encryptedData,
      aesKeyRaw: aesKeyRaw, // Return for manual decryption if needed
    };
  } catch (error) {
    console.error("Encryption failed:", error);
    return null;
  }
};

// ====================== DECRYPT SERVER RESPONSE (USING SAME SESSION KEY) ======================
export const decryptResponse = async (
  encryptedBase64Url,
  responseIvHex,
  customAesKeyRaw,
) => {
  const keyToUse = customAesKeyRaw || lastAesKeyRaw;
  if (!keyToUse) {
    throw new Error("No AES key available! Did you call encryptLogin() first?");
  }

  try {
    if (typeof encryptedBase64Url !== "string") {
      console.warn(
        "decryptResponse received a non-string payload:",
        encryptedBase64Url,
      );
      return encryptedBase64Url;
    }

    // Fix Base64URL → standard Base64
    let base64 = encryptedBase64Url.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) base64 += "=";

    // Use the IV returned by backend (it reuses the same one)
    const ivHexToUse = responseIvHex || lastIvHex;
    if (!ivHexToUse) throw new Error("IV missing");

    // Reconstruct AES key as hex
    const aesKeyHex = Array.from(keyToUse)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    // Decrypt using CryptoJS (exact match with backend encryption)
    const decrypted = CryptoJS.AES.decrypt(
      base64,
      CryptoJS.enc.Hex.parse(aesKeyHex),
      {
        iv: CryptoJS.enc.Hex.parse(ivHexToUse),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) {
      throw new Error("Decryption failed – wrong key or corrupted data");
    }

    return JSON.parse(decryptedText);
  } catch (err) {
    console.error("Decryption failed:", err);
    throw new Error("Failed to decrypt server response");
  }
};

// Optional: Clear session keys (call on logout)
export const clearEncryptionKeys = () => {
  lastAesKeyRaw = null;
  lastIvHex = null;
};
